import { Badge, type BadgeTone } from '@/components/ui/badge'
import type { UserStatus } from '@lib/enums/user-status.enum'
import type { Instant } from '@lib/validators/temporal.validator'

/** Status → tone + label. Shared by the tenant user manager and vendor users. */
const USER_STATUS: Record<UserStatus, { tone: BadgeTone; label: string }> = {
  PENDING: { tone: 'warning', label: 'Invited' },
  ACTIVE: { tone: 'success', label: 'Active' },
  DEACTIVATED: { tone: 'neutral', label: 'Deactivated' },
}

/**
 * An account-status tag: invited (amber), active (green), deactivated (gray).
 *
 * `PENDING` alone means "has not signed in", which for a client says less than it
 * appears to. A client is created without being invited — a Prism import brings
 * hundreds of contacts across, and adding a client to an event by hand does the
 * same — so calling them all "Invited" claimed an email nobody had sent. Where the
 * caller knows whether an invitation was actually issued it passes `invitedAt`, and
 * a client who has never had one reads as **Added**: a real contact, not yet asked
 * to sign in.
 *
 * Omit `invitedAt` where the question cannot arise. Every other role is created by
 * an invitation, so their `PENDING` genuinely means invited.
 */
export function UserStatusBadge({
  status,
  invitedAt,
  inviteExpiresAt,
  inviteKeptOpen,
}: {
  status: UserStatus
  /**
   * When they were last invited; `null` for never, and `undefined` where the caller
   * does not know — which reads as "Invited", the old behavior, rather than
   * claiming nobody was asked.
   */
  invitedAt?: Instant | null | undefined
  /**
   * When the outstanding invitation runs out; `null` when none is outstanding,
   * `undefined` where the caller does not know — which keeps saying "Invited",
   * as before, rather than claiming a live invitation has lapsed.
   */
  inviteExpiresAt?: Instant | null | undefined
  /** A planner's invitation kept open by an event still ahead: not lapsed, whatever its date. */
  inviteKeptOpen?: boolean | undefined
}) {
  const { tone, label } = userStatusBadge(status, invitedAt, inviteExpiresAt, inviteKeptOpen)
  return <Badge tone={tone}>{label}</Badge>
}

/**
 * What the badge says, as a decision rather than a render — the same shape as
 * `inviteActionLabel` below, and testable the same way.
 *
 * Three states share `PENDING`, and telling them apart is the whole job:
 * nobody has written to this person; somebody has and they can still act on it;
 * somebody has and the link has since stopped working. The last one read as
 * plain "Invited", so a client sat waiting for a way in that no longer existed
 * while everybody looking at the list believed they had been asked.
 *
 * `undefined` is "the caller does not know" and must keep the old answer — the
 * lists that hold only roles created *by* an invitation pass nothing at all.
 */
export function userStatusBadge(
  status: UserStatus,
  invitedAt?: Instant | null | undefined,
  inviteExpiresAt?: Instant | null | undefined,
  inviteKeptOpen = false,
): { tone: BadgeTone; label: string } {
  if (status === 'PENDING' && invitedAt === null) {
    return { tone: 'neutral', label: 'Added' }
  }
  if (status === 'PENDING' && invitedAt != null && inviteExpiresAt === null && !inviteKeptOpen) {
    return { tone: 'warning', label: 'Invite expired' }
  }
  return USER_STATUS[status]
}

/**
 * What the invite button should say, from the same fact the badge reads.
 *
 * "Resend" on someone who has never been written to is a small lie of the same
 * kind as calling them Invited, and "Invite" on someone already chased implies a
 * first attempt. Both labels come from here so they cannot drift from each other
 * or from the badge.
 *
 * `undefined` — a caller that does not know — keeps "Resend", which is what those
 * lists have always said and is true for every role that can only be created by
 * an invitation.
 *
 * The object is named because these labels now live in a menu beside "Delete
 * invitation" and "Deactivate". "Send", alone in a list of named actions, does
 * not say what is being sent.
 */
export function inviteActionLabel(
  invitedAt?: Instant | null | undefined,
): 'Send invitation' | 'Resend invitation' {
  return invitedAt === null ? 'Send invitation' : 'Resend invitation'
}
